import '../styles/main.css'
import { createEngine } from '../engine/EngineFactory'
import { Game } from '../game/Game'
import type { TestBridge } from '../game/Game'
import { registerPwa } from '../pwa/register'
import {
  clearOfflineRuntime,
  clearStartupRecoveryMarker,
  formatStartupError,
  requestServiceWorkerRecovery,
} from '../pwa/StartupRecovery'

declare global {
  interface Window {
    __NEON_DESCENT_TEST__?: TestBridge
  }
}

async function bootstrap(): Promise<void> {
  const canvas = document.querySelector<HTMLCanvasElement>('#game-canvas')
  if (canvas === null) throw new Error('Game canvas was not found')
  registerPwa(showSystemStatus, canActivateUpdate)
  if (import.meta.env.DEV && new URLSearchParams(window.location.search).get('debug') === 'fail-startup') {
    throw new Error('Forced startup recovery diagnostic')
  }
  const result = await createEngine(canvas)
  const game = new Game(result.engine, canvas)
  game.start()
  clearStartupRecoveryMarker(sessionStorage)
  if (import.meta.env.DEV || import.meta.env.MODE === 'test') window.__NEON_DESCENT_TEST__ = game.testBridge
}

bootstrap().catch((error: unknown) => {
  console.error('Neon Descent initialization failed', error)
  const message = formatStartupError(error)
  const status = document.querySelector<HTMLElement>('#loading-status')
  if (status !== null) status.textContent = `STARTUP FAILED // ${message}`
  const recoveryButton = document.querySelector<HTMLButtonElement>('#startup-recovery-button')
  if (recoveryButton !== null) {
    recoveryButton.hidden = false
    recoveryButton.addEventListener('click', () => void repairAndReload(recoveryButton), { once: true })
  }
  void requestServiceWorkerRecovery(() => window.location.reload())
})

function showSystemStatus(message: string): void {
  const activeScreen = document.querySelector<HTMLElement>('.screen.is-visible')
  const target = activeScreen?.dataset.screen === 'loading'
    ? document.querySelector<HTMLElement>('#loading-status')
    : document.querySelector<HTMLElement>('#status-toast')
  if (target !== null) target.textContent = message
}

function canActivateUpdate(): boolean {
  const screen = document.querySelector<HTMLElement>('.screen.is-visible')?.dataset.screen
  return screen === 'loading' || screen === 'menu' || screen === 'death'
}

async function repairAndReload(button: HTMLButtonElement): Promise<void> {
  button.disabled = true
  button.textContent = 'Repairing offline cache...'
  const status = document.querySelector<HTMLElement>('#loading-status')
  if (status !== null) status.textContent = 'RESETTING OFFLINE CORE'
  await clearOfflineRuntime(
    'serviceWorker' in navigator ? navigator.serviceWorker : undefined,
    'caches' in window ? window.caches : undefined,
  )
  clearStartupRecoveryMarker(sessionStorage)
  const cleanUrl = new URL(window.location.href)
  cleanUrl.searchParams.delete('debug')
  window.location.replace(cleanUrl)
}
